<template>
  <div class="d-flex align-center justify-space-between flex-wrap ga-4">
    <div class="d-flex align-center">
      <SearchTextField v-model="searchQuery"
                       :disabled="logsStore.roomLogsPending"
                       class="mr-4"
                       :tooltipText="$t('project.Logs.LogsTopBar.searchPlaceholder')"
                       data-locator="logs-search-input"
                       @keydown.enter="onEnterDown"
                       @click:clear="onClearClick"
      />
      <app-button
        type="outlined"
        data-locator="logs-filters-button"
        variant="neutral"
        is-icon-color-custom
        @click="onFiltersButtonClick"
      >
        <template #left-icon>
          <template v-if="logsStore.logsFilters.length === 0">
            <app-material-icon name="instant_mix" />
          </template>
          <template v-else-if="logsStore.logsFilters.length > 0">
            <v-avatar color="primary"
                      class="text-white detail-md-regular"
                      size="19"
            >
              {{ logsStore.logsFilters.length }}
            </v-avatar>
          </template>
        </template>
        {{ $t('common.filters') }}
      </app-button>
    </div>
      <AppDownloadFile :link="exportLogsLink">
        <template #default="{submitForm}">
          <app-button
            data-locator="logs-excel-export-button"
            @click="exportLogs(submitForm)"
          >
            <app-material-icon name="upload" color="icon-inverted" />
            {{ exportButtonText }}
          </app-button>
        </template>
      </AppDownloadFile>
  </div>
</template>

<script lang="ts">
import axios from 'axios'
import { defineComponent } from 'vue'
import { mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { useSearch } from '@/common/search/useSearch'
import SearchTextField from '@/common/SearchTextField.vue'
import { surveyCanBeTriggered, SurveyId, triggerSurvey } from '@/plugins/satismeter'
import { useLogsStore } from '@/stores/logsStore'

const SEARCH_QUERY_DEBOUNCE_TIMER = 1500

export default defineComponent({
  name: 'LogsTopBar',
  components: { AppDownloadFile, SearchTextField },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup: () => {
    const logsStore = useLogsStore()
    const { searchQuery } = useSearch(
      (value: string) => { logsStore.roomLogsSearchQuery = value },
      SEARCH_QUERY_DEBOUNCE_TIMER,
      logsStore.roomLogsSearchQuery,
    )
    return {
      searchQuery,
      logsStore,
    }
  },
  data () {
    return {
      logExportDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    exportLogsLink () {
      return axios.getUri({
        url: `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/logs`,
        params: this.logsStore.getLogsParams,
      })
    },
    exportButtonText () {
      if (this.logsStore.hasActiveFilters || this.logsStore.roomLogsSearchQuery) {
        return this.$t('project.Logs.LogsTopBar.exportResults')
      }
      return this.$t('project.Logs.LogsTopBar.exportAllActivity')
    },
  },
  methods: {
    onFiltersButtonClick () {
      this.logsStore.logsFiltersPaneIsOpen = true
    },
    onEnterDown (event: Event) {
      this.logsStore.roomLogsSearchQuery = (event.target as HTMLInputElement).value
    },
    onClearClick () {
      this.logsStore.roomLogsSearchQuery = ''
    },
    exportLogs (submitFormCallback: () => void) {
      const { id: userId, fullName, email, isSubUser } = this.profile
      if (surveyCanBeTriggered(SurveyId.NPS, userId)) {
        triggerSurvey(SurveyId.NPS, userId, fullName, email, isSubUser)
      }
      submitFormCallback()
    },
  },
})
</script>
